// File: src/components/dashboard/ArtistChartingSingleCard.js

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { 
  getFirestore, 
  doc, 
  onSnapshot, 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  setDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { useProfile } from '../../context/ProfileContext';
import confetti from 'canvas-confetti';

// Import nuclear certification icons
import radiumIcon from '../../assets/radium.png';
import plutoniumIcon from '../../assets/plutonium.png';
import uraniumIcon from '../../assets/uranium.png';

// Import Chart Badges
import theNuclearFifty from '../../assets/theNuclearFifty.png';
import theNuclearFiftyDark from '../../assets/theNuclearFifty_darkmode.png';
import theTopTwenty from '../../assets/theTopTwenty.png';
import theTopTwentyDark from '../../assets/theTopTwenty_darkmode.png';

// Asset & Modal for Promote Higher
import tipWhite from '../../assets/tip_white.png';
import PromoteSingleModal from '../artistDashboard/chartingServices/PromoteSingleModal';

const CONFETTI_COLORS = ['#ff00cc', '#a855f7', '#3b82f6', '#22c55e', '#eab308', '#f97316'];

const ArtistChartingSingleCard = ({ onBoostClick, catalogSongs = [] }) => {
  const { activeProfile, isDarkMode } = useProfile();
  const db = getFirestore();
  const artistId = activeProfile?.type === 'artist' ? activeProfile.id : null;

  const [chartingSong, setChartingSong] = useState(null);
  const [promotedSongId, setPromotedSongId] = useState(null);
  const [artistCatalog, setArtistCatalog] = useState(catalogSongs);
  const [loading, setLoading] = useState(true);

  // Promote Higher Modal State
  const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);

  // Search & Dropdown State
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const dropdownRef = useRef(null);

  // Confetti Celebration Refs
  const cardRef = useRef(null);
  const hasTriggeredChartConfetti = useRef(false);

  // 1. Dynamic listener for latest weekly chart
  useEffect(() => {
    if (!artistId) {
      setChartingSong(null);
      setLoading(false);
      return;
    }

    let unsubscribeDisplay = null;

    const chartsQuery = query(
      collection(db, 'weeklyCharts'),
      orderBy('__name__', 'desc'),
      limit(1)
    );

    const unsubscribeWeekly = onSnapshot(chartsQuery, (snapshot) => {
      if (snapshot.empty) {
        setChartingSong(null);
        setLoading(false);
        return;
      }

      const latestWeekDocId = snapshot.docs[0].id;
      const displayDocRef = doc(db, 'weeklyCharts', latestWeekDocId, 'displays', 'topFifty');

      if (unsubscribeDisplay) unsubscribeDisplay();

      unsubscribeDisplay = onSnapshot(displayDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const songs = docSnap.data().songs || [];
          const artistSongsOnChart = songs
            .filter(s => s.artistId === artistId)
            .sort((a, b) => (a.position || 999) - (b.position || 999));

          if (artistSongsOnChart.length > 0) {
            setChartingSong(artistSongsOnChart[0]);
          } else {
            setChartingSong(null);
          }
        } else {
          setChartingSong(null);
        }
        setLoading(false);
      }, (err) => {
        console.error("Error fetching display doc:", latestWeekDocId, err);
        setLoading(false);
      });
    }, (err) => {
      console.error("Error fetching latest weekly chart document:", err);
      setLoading(false);
    });

    return () => {
      unsubscribeWeekly();
      if (unsubscribeDisplay) unsubscribeDisplay();
    };
  }, [artistId, db]);

  // 2. Sequenced Confetti Explosion (Delayed to 800ms)
  useEffect(() => {
    if (chartingSong && !hasTriggeredChartConfetti.current) {
      hasTriggeredChartConfetti.current = true;

      const timer = setTimeout(() => {
        if (cardRef.current) {
          const rect = cardRef.current.getBoundingClientRect();
          const originX = (rect.left + rect.width / 2) / window.innerWidth;
          const originY = (rect.top + rect.height / 2) / window.innerHeight;

          confetti({
            particleCount: 55,
            spread: 70,
            origin: { x: originX, y: originY },
            colors: CONFETTI_COLORS,
            disableForReducedMotion: true,
            zIndex: 1000,
          });
        }
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [chartingSong]);

  // 3. Sync promoted song from artist_accounts
  useEffect(() => {
    if (!artistId) return;

    const artistRef = doc(db, 'artist_accounts', artistId);
    const unsubscribe = onSnapshot(artistRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setPromotedSongId(data.featuredPromoteSongId || null);
      }
    }, (err) => {
      console.error("Error reading featured promote song from artist account:", err);
    });

    return () => unsubscribe();
  }, [artistId, db]);

  // 4. Populate artist catalog
  useEffect(() => {
    if (catalogSongs && catalogSongs.length > 0) {
      setArtistCatalog(catalogSongs);
      return;
    }

    if (!artistId) return;

    const fetchCatalog = async () => {
      try {
        const q = query(
          collection(db, 'songs'),
          where('artistId', '==', artistId),
          where('isDeleted', '==', false)
        );
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setArtistCatalog(list);
      } catch (err) {
        console.error("Error fetching catalog for promotion dropdown:", err);
      }
    };

    fetchCatalog();
  }, [artistId, catalogSongs, db]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCatalog = useMemo(() => {
    if (!searchTerm.trim()) return artistCatalog;
    return artistCatalog.filter(song =>
      (song.title || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, artistCatalog]);

  const activeSong = useMemo(() => {
    if (chartingSong) return chartingSong;
    if (promotedSongId && artistCatalog.length > 0) {
      return artistCatalog.find(s => s.id === promotedSongId) || null;
    }
    return null;
  }, [chartingSong, promotedSongId, artistCatalog]);

  const handleSelectSongToPromote = async (song) => {
    if (!artistId) return;
    try {
      const artistRef = doc(db, 'artist_accounts', artistId);
      await setDoc(artistRef, {
        featuredPromoteSongId: song.id,
        promotedSongUpdatedAt: serverTimestamp()
      }, { merge: true });

      setPromotedSongId(song.id);
      setIsDropdownVisible(false);
      setSearchTerm('');
    } catch (err) {
      console.error("Failed to persist selected promote song:", err);
    }
  };

  const handleClearPromotedSong = async () => {
    if (!artistId) return;
    try {
      const artistRef = doc(db, 'artist_accounts', artistId);
      await setDoc(artistRef, {
        featuredPromoteSongId: null,
        promotedSongUpdatedAt: serverTimestamp()
      }, { merge: true });

      setPromotedSongId(null);
    } catch (err) {
      console.error("Failed to clear promoted song:", err);
    }
  };

  // Nuclear Progress calculation
  const nuclearProgress = useMemo(() => {
    if (!activeSong) return { progress: 0, nextAwardName: "Radium", icon: radiumIcon, currentDownloads: 0, nextGoal: 10 };

    const downloads = activeSong.stats?.totalDownloads || activeSong.totalDownloads || activeSong.downloads || 0;

    const tiers = {
      radium: { name: "Radium", downloads: 10, icon: radiumIcon },
      plutonium: { name: "Plutonium", downloads: 50, icon: plutoniumIcon },
      uranium: { name: "Uranium", downloads: 100, icon: uraniumIcon },
    };

    let nextAward = tiers.radium;
    let prevDownloads = 0;

    if (downloads >= 10 && downloads < 50) {
      nextAward = tiers.plutonium;
      prevDownloads = 10;
    } else if (downloads >= 50 && downloads < 100) {
      nextAward = tiers.uranium;
      prevDownloads = 50;
    } else if (downloads >= 100) {
      nextAward = null;
    }

    const progressPercent = nextAward 
      ? Math.min(100, Math.floor(((downloads - prevDownloads) / (nextAward.downloads - prevDownloads)) * 100))
      : 100;

    return {
      progress: progressPercent,
      nextAwardName: nextAward ? nextAward.name : "All Unlocked",
      icon: downloads >= 100 ? uraniumIcon : downloads >= 50 ? plutoniumIcon : radiumIcon,
      currentDownloads: downloads,
      nextGoal: nextAward ? nextAward.downloads : 100,
    };
  }, [activeSong]);

  if (loading) {
    return (
      <div className={`p-4 rounded-xl border text-center animate-pulse ${
        isDarkMode ? 'bg-zinc-900 border-zinc-800 text-zinc-500' : 'bg-white border-gray-100 text-gray-400'
      }`}>
        <p className="text-xs font-black uppercase tracking-widest">Checking Chart Standings...</p>
      </div>
    );
  }

  // 🌟 CASE 1: No song charting and no track selected in database
  if (!activeSong) {
    return (
      <div className={`px-4 pt-2.5 pb-4 rounded-xl border shadow-sm space-y-3 transition-all ${
        isDarkMode ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-white border-gray-200 text-slate-900'
      }`}>
        {/* Top Underlined Header */}
        <div>
          <h3 className={`text-[11px] font-black uppercase tracking-wider underline underline-offset-4 ${
            isDarkMode ? 'text-zinc-100 decoration-brand-pink' : 'text-slate-900 decoration-brand-pink'
          }`}>
            No Currently Charting Singles!
          </h3>
        </div>

        {/* Primary CTA Label & Dropdown Search */}
        <div className="relative" ref={dropdownRef}>
          {/* Prominent, enlarged CTA title */}
          <label className="block text-sm sm:text-base font-black text-slate-900 dark:text-white mb-2 tracking-tight">
            Select A Song For AirPlay
          </label>

          <div className="relative flex items-center">
            {/* Search Input Field */}
            <input
              type="text"
              value={searchTerm}
              onFocus={() => setIsDropdownVisible(true)}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setIsDropdownVisible(true);
              }}
              placeholder="Search catalog to select a song for charting..."
              className={`w-full py-2.5 pl-9 pr-9 text-xs font-bold rounded-xl border transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-pink/30 ${
                isDarkMode 
                  ? "bg-zinc-950 border-zinc-700 text-white focus:border-brand-pink placeholder-zinc-500" 
                  : "bg-gray-50 border-gray-300 text-gray-900 focus:border-brand-pink placeholder-gray-400"
              }`}
            />

            {/* Magnifying Glass Icon */}
            <svg 
              className="absolute left-3 w-4 h-4 text-brand-pink" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>

            {/* Clear Button (Visible only when user types) */}
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="absolute right-3 text-zinc-400 hover:text-white text-xs px-1"
                aria-label="Clear search"
              >
                &times;
              </button>
            )}
          </div>

          {/* Discography Dropdown Menu */}
          {isDropdownVisible && (
            <div 
              className={`absolute top-full left-0 right-0 mt-1.5 border rounded-xl shadow-2xl max-h-52 overflow-y-auto z-50 divide-y transition-all ${
                isDarkMode 
                  ? "bg-zinc-900 border-zinc-700 divide-zinc-800 text-zinc-200 shadow-black/80" 
                  : "bg-white border-gray-200 divide-gray-100 text-gray-800 shadow-slate-300/60"
              }`}
            >
              {filteredCatalog.length > 0 ? (
                filteredCatalog.map((song) => {
                  const hasCharted = 
                    Boolean(song.isBackCatalogue) || 
                    (song.chartHistory && Object.keys(song.chartHistory).length > 0);

                  return (
                    <div
                      key={song.id}
                      onMouseDown={() => {
                        if (!hasCharted) {
                          handleSelectSongToPromote(song);
                        }
                      }}
                      className={`p-2.5 flex items-center justify-between text-xs transition-colors ${
                        hasCharted
                          ? "opacity-40 cursor-not-allowed bg-zinc-950/40"
                          : isDarkMode
                            ? "hover:bg-zinc-800 cursor-pointer"
                            : "hover:bg-gray-50 cursor-pointer"
                      }`}
                    >
                      <span className="font-bold truncate pr-2">{song.title}</span>

                      {hasCharted ? (
                        <span className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 border border-zinc-700/50 flex-shrink-0">
                          Already Charted
                        </span>
                      ) : (
                        <span className="text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 flex-shrink-0">
                          Eligible
                        </span>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="p-3 text-center text-xs text-zinc-500">
                  No matching tracks found in your catalog.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // 🌟 CASE 2: Single is either officially charting OR selected by the user to promote
  const isOfficialChart = Boolean(chartingSong);
  const position = activeSong.position;
  const isTopTwenty = isOfficialChart && position <= 20;

  const chartBadgeSrc = isTopTwenty
    ? (isDarkMode ? theTopTwentyDark : theTopTwenty)
    : (isDarkMode ? theNuclearFiftyDark : theNuclearFifty);

  return (
    <>
      <div 
        ref={cardRef}
        className={`px-4 pt-2.5 pb-3.5 rounded-xl border shadow-sm transition-all ${
          isDarkMode ? 'bg-zinc-900 border-zinc-800 text-white' : 'bg-white border-gray-200 text-slate-900'
        }`}
      >
        {/* 🌟 1. SECTION TITLE (Matching bold underline style for both states) */}
        <div className="mb-2">
          <h3 className={`text-[11px] font-black uppercase tracking-wider underline underline-offset-4 ${
            isDarkMode ? 'text-zinc-100 decoration-brand-pink' : 'text-slate-900 decoration-brand-pink'
          }`}>
            {isOfficialChart ? 'Currently Charting Single!' : 'Selected Track For Charting'}
          </h3>
        </div>

        {/* 🌟 2. TOP ACTION ROW */}
        <div className="flex items-center justify-between gap-3 mb-2.5">
          {/* Left: Album Artwork */}
          <div className="relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden shadow-sm border border-zinc-700/50 bg-black">
            <img 
              src={activeSong.albumArtworkURL || activeSong.artworkURL || ''} 
              alt={activeSong.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Center: Chart Badge + Large #Rank OR Bold "Not Ranked" with Change button */}
          <div className="flex-grow min-w-0 flex flex-col justify-center">
            {isOfficialChart ? (
              <>
                <div>
                  <img 
                    src={chartBadgeSrc} 
                    alt={isTopTwenty ? "The Top Twenty" : "The Nuclear Fifty"} 
                    className="h-4 sm:h-5 w-auto object-contain"
                  />
                </div>

                <div className="mt-1">
                  <span className="text-2xl sm:text-3xl font-black text-emerald-400 leading-none tracking-tight">
                    #{position}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex flex-col justify-center">
                {/* Bold, Large "Not Ranked" */}
                <span className="text-xl sm:text-2xl font-black text-amber-500 leading-none tracking-tight">
                  Not Ranked
                </span>

                {/* Change Track Button (Always visible on mobile & desktop) */}
                <div className="mt-1">
                  <button 
                    onClick={handleClearPromotedSong}
                    className="text-[10px] font-bold text-gray-400 hover:text-brand-pink dark:hover:text-white underline underline-offset-2 transition-colors inline-block"
                  >
                    Change Track
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right: Promote Higher Button */}
          
          <div className="flex-shrink-0 flex items-center">
            <button
              onClick={() => setIsPromoteModalOpen(true)}
              className="bg-brand-pink text-white text-[11px] font-black px-3 py-2 rounded-lg shadow-md shadow-brand-pink/25 hover:scale-105 active:scale-95 transition-all duration-200 flex items-center gap-1.5"
            >
              <img src={tipWhite} alt="Promote" className="w-3.5 h-3.5 object-contain" />
              <span>Promote Higher</span>
            </button>
          </div>
          
        </div>

        {/* 🌟 3. SONG TITLE */}
        <div className="mb-2.5">
          <h4 className="text-xs sm:text-sm font-black truncate leading-snug">
            "{activeSong.title}"
          </h4>
        </div>

        {/* 🌟 4. BOTTOM ROW: Nuclear Certification Progress Bar */}
        <div className="pt-2 border-t border-zinc-200 dark:border-zinc-800/60 space-y-1">
          <div className="flex justify-between items-center text-[9px] font-black">
            <div className="flex items-center gap-1">
              <img src={nuclearProgress.icon} alt="Nuclear Icon" className="w-3.5 h-3.5 object-contain" />
              <span className="opacity-60 uppercase tracking-wider">
                Nuclear Progress ({nuclearProgress.currentDownloads}/{nuclearProgress.nextGoal} DLs)
              </span>
            </div>
            <span className="text-brand-pink drop-shadow-[0_0_8px_rgba(236,72,153,0.5)]">
              {nuclearProgress.progress}%
            </span>
          </div>

          <div className={`w-full rounded-full h-2 p-[1.5px] ${
            isDarkMode ? "bg-zinc-800/80" : "bg-gray-200"
          }`}>
            <div
              className="h-full rounded-full bg-gradient-to-r from-emerald-400 via-amber-400 to-brand-pink transition-all duration-500 shadow-[0_0_10px_rgba(236,72,153,0.7)]"
              style={{ width: `${nuclearProgress.progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* Promote Single Modal */}
      <PromoteSingleModal
        isOpen={isPromoteModalOpen}
        onClose={() => setIsPromoteModalOpen(false)}
        song={activeSong}
        artistName={activeProfile?.name || activeProfile?.artistName}
        lifetimePoints={activeProfile?.lifetimePoints || 0}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default ArtistChartingSingleCard;